import React from 'react';
import { useNavigate } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
    const navigate = useNavigate();

    return (
        <div className="privacy-container">
            <div className="privacy-header">
                <button onClick={() => navigate('/')} className="back-btn">← Back to Home</button>
                <h1>Privacy Policy</h1>
                <p className="last-updated">Last Updated: March 11, 2026</p>
            </div>

            <div className="privacy-content">
                <section className="privacy-section">
                    <h2>1. Introduction</h2>
                    <p>
                        This Privacy Policy describes how Launchway collects, uses, and protects data when you use the Launchway web app and CLI.
                    </p>
                    <p>
                        We keep this page aligned with the product behavior currently implemented in the app and backend.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>2. Data We Collect</h2>
                    <h3>2.1 Account Data</h3>
                    <ul>
                        <li>Email address, first name, last name</li>
                        <li>Password hash (passwords are not stored as plain text)</li>
                        <li>Email verification state and account timestamps</li>
                    </ul>

                    <h3>2.2 Profile Information</h3>
                    <ul>
                        <li>Contact information and personal details you enter in Profile</li>
                        <li>Education, experience, projects, skills, links, and resume references</li>
                        <li>Job preferences and optional equal-opportunity answers</li>
                    </ul>

                    <h3>2.3 Job Application Data</h3>
                    <ul>
                        <li>Job URLs/IDs, job/company metadata, and application status/history</li>
                        <li>Credit usage and feature usage counters for service limits</li>
                    </ul>

                    <h3>2.4 Integration Data</h3>
                    <ul>
                        <li>Google OAuth tokens (encrypted) when you connect Google</li>
                        <li>Custom Gemini API key (encrypted) only if you provide one</li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>3. How We Use Your Information</h2>
                    <ul>
                        <li>Authenticate users and protect accounts</li>
                        <li>Store and process profile data needed for CLI and web app features</li>
                        <li>Run AI-powered features such as parsing, tailoring, and field assistance</li>
                        <li>Provide credits, rate limits, and usage tracking</li>
                        <li>Send required account emails (for example verification and account notifications)</li>
                        <li>Detect abuse and operate the service safely</li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>4. Third-Party Services</h2>
                    <p>Launchway relies on third-party providers. Data is sent only as needed for the feature you use:</p>

                    <h3>4.1 Google Services</h3>
                    <ul>
                        <li>Google OAuth and Google Docs/Drive APIs for connected document workflows</li>
                        <li>Google Gemini APIs for AI-driven functionality</li>
                    </ul>

                    <h3>4.2 Resend (Email Service)</h3>
                    <ul>
                        <li>Used to send account emails</li>
                        <li><a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">Resend Privacy Policy</a></li>
                    </ul>

                    <h3>4.3 Supabase (Database Hosting)</h3>
                    <ul>
                        <li>Used for PostgreSQL data hosting</li>
                        <li><a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">Supabase Privacy Policy</a></li>
                    </ul>

                    <h3>4.4 Job Boards and ATS Platforms</h3>
                    <ul>
                        <li>Application data is submitted to third-party job sites when you run apply flows</li>
                    </ul>

                </section>

                <section className="privacy-section">
                    <h2>5. Security</h2>
                    <ul>
                        <li>Passwords are stored as bcrypt hashes</li>
                        <li>Sensitive external credentials/tokens are encrypted before database storage</li>
                        <li>Authentication is token-based (JWT)</li>
                        <li>Rate limiting and login-attempt protections are used to reduce abuse</li>
                        <li>Data is transmitted over HTTPS/TLS in normal deployment</li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>6. Browser Storage</h2>
                    <p>The web app stores session details in browser localStorage:</p>
                    <ul>
                        <li><strong>auth_token</strong> for authenticated API requests</li>
                        <li><strong>user</strong> object for UI session state</li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>7. Your Controls</h2>
                    <p>Current controls available in product include:</p>
                    <ol>
                        <li>Update account and profile data in the app</li>
                        <li>Export account data as JSON through account export functionality</li>
                        <li>Delete account with password confirmation</li>
                        <li>Disconnect optional integrations (for example Google)</li>
                    </ol>
                </section>

                <section className="privacy-section">
                    <h2>8. Data Retention</h2>
                    <p>
                        We keep account and profile data while your account is active. When account deletion is completed, associated user data is removed from active records. Some operational logs or backups may persist for a limited period required for security and reliability.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>9. Children&apos;s Privacy</h2>
                    <p>Launchway is intended for users who are at least 18 years old.</p>
                </section>

                <section className="privacy-section">
                    <h2>10. Changes to This Privacy Policy</h2>
                    <p>We may update this Privacy Policy as the product evolves.</p>
                    <ul>
                        <li>Updating the "Last Updated" date at the top of this policy</li>
                        <li>Posting updates in the web app or website for significant changes</li>
                    </ul>
                </section>

                <section className="privacy-section">
                    <h2>11. Contact Us</h2>
                    <p>If you have privacy questions, contact:</p>
                    <div className="contact-info">
                        <p><strong>Email:</strong> launchway@tailor-mycareer.online</p>
                    </div>
                </section>

                <div className="privacy-footer">
                    <p>This policy applies to Launchway web and CLI services.</p>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
